import { useRef, useEffect } from "react";
import { TextB, TextItalic, TextUnderline, ListBullets, LinkSimple, TextT } from "@phosphor-icons/react";

const SIZES = [10, 11, 12, 13, 14, 16, 18, 20];

export function RichTextEditor({ value, onChange, placeholder = "Écrivez votre message…", testid, minHeight = 180 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Sync external value changes (IA, modèle, valeur par défaut) mais JAMAIS pendant
    // la frappe (sinon le curseur saute) : on ne réécrit que si l'éditeur n'a pas le focus.
    if (document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange?.(ref.current?.innerHTML || "");
  const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); emit(); };
  const addLink = () => {
    const url = window.prompt("Adresse du lien (https://…) :");
    if (url) { document.execCommand("createLink", false, url); emit(); }
  };

  // Applique une taille de police en styles INLINE (compatible clients email : Gmail/Outlook/Apple).
  const applyFontSize = (px) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7"); // taille sentinelle -> <font size="7">
    el.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      span.innerHTML = f.innerHTML;
      f.replaceWith(span);
    });
    emit();
  };

  return (
    <div className="border border-border rounded-md bg-surface-2 overflow-hidden">
      <style>{`.ce-editor:empty:before{content:attr(data-ph);color:#8a8a93;}
        .ce-editor ul{list-style:disc;padding-left:1.25rem;margin:.25rem 0;}
        .ce-editor a{color:#00E5FF;text-decoration:underline;}`}</style>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-surface/40 flex-wrap">
        <ToolBtn onClick={() => exec("bold")} icon={TextB} title="Gras" testid={`${testid}-bold`} />
        <ToolBtn onClick={() => exec("italic")} icon={TextItalic} title="Italique" testid={`${testid}-italic`} />
        <ToolBtn onClick={() => exec("underline")} icon={TextUnderline} title="Souligné" testid={`${testid}-underline`} />
        <ToolBtn onClick={() => exec("insertUnorderedList")} icon={ListBullets} title="Liste à puces" testid={`${testid}-list`} />
        <ToolBtn onClick={addLink} icon={LinkSimple} title="Insérer un lien" testid={`${testid}-link`} />
        <div className="flex items-center gap-1 ml-1 pl-2 border-l border-border">
          <TextT size={13} className="text-muted" />
          <select
            data-testid={`${testid}-fontsize`}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => { const v = e.target.value; if (v) { applyFontSize(Number(v)); e.target.value = ""; } }}
            defaultValue=""
            className="bg-surface border border-border rounded text-xs text-fg px-1 py-1 outline-none cursor-pointer"
            title="Taille du texte sélectionné"
          >
            <option value="">Taille</option>
            {SIZES.map((s) => <option key={s} value={s}>{s} px</option>)}
          </select>
        </div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testid}
        data-ph={placeholder}
        onInput={emit}
        onBlur={emit}
        style={{ minHeight: `${minHeight}px` }}
        className="ce-editor max-h-[320px] overflow-y-auto px-3 py-2.5 text-sm text-fg leading-relaxed outline-none"
      />
    </div>
  );
}

function ToolBtn({ onClick, icon: Icon, title, testid }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-1.5 rounded text-muted hover:text-accent hover:bg-surface-2 transition-colors"
    >
      <Icon size={15} weight="bold" />
    </button>
  );
}
